import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { BRIEF_ACCEPTED_FILE_TYPES, formatBriefFileSize } from '../../utils/kkpBriefFichierUtils';
import { STATUT_CREA_CLIENT, STATUT_BAT_ENVOYE, STATUT_BAT_VALIDE, isCreaClientStatut } from '../../utils/kkpBatValide';
import { AppAlert, AppButton, AppFormField, AppModal, AppPillSwitch } from '../ui';
import '../../styles/kkp-brief-fichiers.scss';

function fileIconFromName(name = '') {
  const lower = name.toLowerCase();
  if (/\.(jpe?g|png|gif|webp|svg)$/.test(lower)) return 'mdi:file-image-outline';
  if (/\.pdf$/.test(lower)) return 'mdi:file-pdf-box';
  if (/\.(doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/.test(lower)) return 'mdi:file-document-outline';
  return 'mdi:paperclip';
}

const KkpCreaPubValidationModal = ({
  show,
  row,
  onClose,
  onSubmit,
  submitting = false,
  error = null,
}) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [statutPhase, setStatutPhase] = useState(STATUT_BAT_ENVOYE);

  useEffect(() => {
    if (show) {
      setFiles([]);
      setDragActive(false);
      setStatutPhase(STATUT_BAT_ENVOYE);
    }
  }, [show, row?.id]);

  const handleClose = () => {
    if (submitting) return;
    setFiles([]);
    setDragActive(false);
    setStatutPhase(STATUT_BAT_ENVOYE);
    onClose?.();
  };

  const addFiles = (fileList) => {
    const picked = Array.from(fileList ?? []);
    if (picked.length === 0) return;
    setFiles((prev) => [...prev, ...picked]);
  };

  const handlePickFiles = (event) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (submitting) return;
    addFiles(event.dataTransfer?.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!submitting) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0 || submitting) return;
    const nextStatut = isCreaClientStatut(row?.statut_phase) ? statutPhase : STATUT_BAT_ENVOYE;
    await onSubmit?.(files, nextStatut);
    setFiles([]);
    setStatutPhase(STATUT_BAT_ENVOYE);
  };

  if (!show || !row) return null;

  const canChooseStatut = isCreaClientStatut(row.statut_phase);

  return (
    <AppModal
      show
      size="lg"
      centered
      onClose={handleClose}
      closeDisabled={submitting}
      headerClassName="align-items-center"
      title={(
        <span className="d-flex align-items-center gap-2 flex-wrap">
          <span>Valider la créa — {row.client_label}</span>
          <span className="badge kkp-pub-graphiste-canal-badge">{row.canal_label}</span>
        </span>
      )}
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={handleClose} disabled={submitting}>Annuler</AppButton>
          <AppButton
            variant="success"
            icon="mdi:check"
            loading={submitting}
            loadingLabel="Validation…"
            onClick={handleSubmit}
            disabled={submitting || files.length === 0}
          >
            {canChooseStatut ? `Valider — ${statutPhase}` : 'Valider — BAT Envoyé'}
          </AppButton>
        </>
      )}
    >
            <dl className="row g-2 mb-3 kkp-pub-graphiste-brief-meta">
              <div className="col-sm-6">
                <dt className="text-secondary-light">Format</dt>
                <dd className="mb-0">{row.format || '—'}</dd>
              </div>
              <div className="col-sm-6">
                <dt className="text-secondary-light">Contenu</dt>
                <dd className="mb-0">{row.contenu || '—'}</dd>
              </div>
              <div className="col-sm-6">
                <dt className="text-secondary-light">Période</dt>
                <dd className="mb-0">{row.periode_label || '—'}</dd>
              </div>
              <div className="col-sm-6">
                <dt className="text-secondary-light">Statut</dt>
                <dd className="mb-0">{row.statut_phase || '—'}</dd>
              </div>
            </dl>

            {canChooseStatut && (
              <AppFormField
                label="Statut après validation"
                required
                className="mb-3"
                hint={
                  statutPhase === STATUT_BAT_VALIDE
                    ? 'Le commercial sera notifié par e-mail avec le visuel.'
                    : 'Le BAT est prêt à être envoyé au client.'
                }
              >
                <AppPillSwitch
                  ariaLabel="Statut après validation"
                  value={statutPhase}
                  onChange={setStatutPhase}
                  disabled={submitting}
                  options={[
                    { value: STATUT_BAT_ENVOYE, label: 'BAT Envoyé', icon: 'mdi:email-fast-outline' },
                    { value: STATUT_BAT_VALIDE, label: 'BAT Validé', icon: 'mdi:check-decagram-outline' },
                  ]}
                />
              </AppFormField>
            )}

            <div className="kkp-brief-fichiers">
              <AppFormField label="Créations à déposer" required className="mb-0">
                <div
                  className={[
                    'kkp-brief-fichiers__dropzone',
                    dragActive ? 'is-active' : '',
                    files.length > 0 ? 'has-files' : '',
                  ].filter(Boolean).join(' ')}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => !submitting && inputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (!submitting) inputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Zone de dépôt de fichiers"
                >
                  <input
                    ref={inputRef}
                    type="file"
                    className="d-none"
                    multiple
                    accept={BRIEF_ACCEPTED_FILE_TYPES}
                    onChange={handlePickFiles}
                  />

                  {files.length === 0 ? (
                    <div className="kkp-brief-fichiers__dropzone-empty">
                      <Icon icon="mdi:cloud-upload-outline" className="kkp-brief-fichiers__dropzone-icon" />
                      <span className="kkp-brief-fichiers__dropzone-text">
                        Glissez-déposez vos fichiers ici ou cliquez pour parcourir
                      </span>
                    </div>
                  ) : (
                    <ul className="kkp-brief-fichiers__list mb-0">
                      {files.map((file, index) => (
                        <li key={`${file.name}-${file.size}-${index}`} className="kkp-brief-fichiers__item">
                          <Icon icon={fileIconFromName(file.name)} className="kkp-brief-fichiers__icon" width={20} />
                          <div className="kkp-brief-fichiers__meta">
                            <div className="kkp-brief-fichiers__name">{file.name}</div>
                            <div className="kkp-brief-fichiers__size">{formatBriefFileSize(file.size)}</div>
                          </div>
                          <AppButton
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            icon="mdi:close"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRemove(index);
                            }}
                            disabled={submitting}
                            title="Retirer"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </AppFormField>
            </div>

            {error && <AppAlert className="mt-3 mb-0">{error}</AppAlert>}
    </AppModal>
  );
};

export default KkpCreaPubValidationModal;
